import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import EditableList from '../EditableList/EditableList';
import useAiProposal from '../../hooks/useAiProposal';
import AiProposalDetailDialog from '../AiProposal/AiProposalDetailDialog';
import AiProposalDecisionDialog from '../AiProposal/AiProposalDecisionDialog';
import AiProposalBulkDecisionDialog from '../AiProposal/AiProposalBulkDecisionDialog';
import { getAiProposalStatusLabel } from '../AiProposal/aiProposalStatus';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const RESOURCE_TYPE = 'aiProposal';
const DEFAULT_LIST_PARAMS = { limit: 50, status: 'pending' };

function formatDateTime(value) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
}

// Tells the EditableList to reload the proposals from the server
function refreshProposalList() {
  window.dispatchEvent(
    new CustomEvent('editableListRefresh', { detail: { resource: RESOURCE_TYPE } })
  );
}

function AiProposalsView() {
  const authCtx = useContext(AuthContext);
  const { getAiProposalById, approveAiProposal, rejectAiProposal } = useAiProposal();

  const [statusFilter, setStatusFilter] = useState('pending');
  const [listParams, setListParams] = useState(DEFAULT_LIST_PARAMS);
  const [detailDialog, setDetailDialog] = useState({ open: false, proposal: null });
  const [detailLoading, setDetailLoading] = useState(false);
  const [decisionDialog, setDecisionDialog] = useState({ open: false, mode: 'approve', item: null });
  // Multi-select for the bulk decision. EditableList owns the rows and only hands the view their
  // ids, so every row it renders is recorded here on the way past — the bulk dialog needs the
  // summary and the status, not just the id. A ref, because this is filled during render.
  const [selectedIds, setSelectedIds] = useState([]);
  const rowsByIdRef = useRef(new Map());
  const [bulkDialog, setBulkDialog] = useState({ open: false, mode: 'approve', proposals: [] });

  const domainInputRef = useRef(null);

  const canRead = authCtx.checkRight('aiProposal:read');
  const canApprove = authCtx.checkRight('aiProposal:approve');
  const canReject = authCtx.checkRight('aiProposal:reject');

  const applyFilters = useCallback(() => {
    const domainValue = domainInputRef.current?.value?.trim() || '';
    const nextParams = { limit: 50 };
    if (statusFilter) {
      nextParams.status = statusFilter;
    }
    if (domainValue) {
      nextParams.targetDomain = domainValue;
    }
    setListParams(nextParams);
  }, [statusFilter]);

  const resetFilters = useCallback(() => {
    if (domainInputRef.current) domainInputRef.current.value = '';
    setStatusFilter('pending');
    setListParams(DEFAULT_LIST_PARAMS);
  }, []);

  const openDetails = useCallback(
    async item => {
      if (!item?._id) return;
      setDetailDialog({ open: true, proposal: null });
      setDetailLoading(true);
      try {
        const response = await getAiProposalById(item._id);
        setDetailDialog({ open: true, proposal: response?.aiProposal || null });
      } catch {
        // Toast is shown inside the hook
        setDetailDialog({ open: false, proposal: null });
      } finally {
        setDetailLoading(false);
      }
    },
    [getAiProposalById]
  );

  const closeDetails = useCallback(() => {
    setDetailDialog({ open: false, proposal: null });
  }, []);

  const openDecision = useCallback((mode, item) => {
    setDecisionDialog({ open: true, mode, item });
  }, []);

  const closeDecision = useCallback(() => {
    setDecisionDialog({ open: false, mode: 'approve', item: null });
  }, []);

  // Executes the approve/reject call, shows the outcome toast and refreshes the list
  const handleDecision = useCallback(
    async (proposal, mode, note) => {
      if (!proposal?._id) return;
      const response =
        mode === 'approve'
          ? await approveAiProposal(proposal._id, note)
          : await rejectAiProposal(proposal._id, note);

      closeDecision();
      closeDetails();

      // null means the request was stored for step-up authentication
      if (response === null) return;

      if (mode === 'approve') {
        const updated = response?.aiProposal;
        if (updated?.status === 'failed') {
          toast.warning(
            'Vorschlag angenommen, aber Anwendung fehlgeschlagen: ' +
              (updated?.applyResult?.error || 'Unbekannter Fehler')
          );
        } else {
          toast.success('Vorschlag angenommen und angewendet');
        }
      } else {
        toast.success('Vorschlag abgelehnt');
      }
      refreshProposalList();
    },
    [approveAiProposal, rejectAiProposal, closeDecision, closeDetails]
  );

  // Only still-open proposals can be decided; EditableList puts a checkbox on every row, so the
  // actionable subset is worked out here rather than by hiding checkboxes.
  const actionableProposals = useMemo(
    () =>
      selectedIds
        .map(id => rowsByIdRef.current.get(id))
        .filter(item => item && item.status === 'pending'),
    [selectedIds]
  );

  const openBulk = useCallback(
    mode =>
      setBulkDialog({
        open: true,
        mode,
        proposals: selectedIds
          .map(id => rowsByIdRef.current.get(id))
          .filter(item => item && item.status === 'pending'),
      }),
    [selectedIds]
  );
  const closeBulk = useCallback(() => setBulkDialog(prev => ({ ...prev, open: false })), []);

  // One proposal of a bulk run. Returns the raw hook response so the dialog can tell a stored
  // step-up request (null) from a decision whose change could not be applied.
  const runBulkOne = useCallback(
    async (id, note) =>
      bulkDialog.mode === 'approve'
        ? approveAiProposal(id, note)
        : rejectAiProposal(id, note),
    [bulkDialog.mode, approveAiProposal, rejectAiProposal]
  );

  const handleBulkFinished = useCallback(decidedIds => {
    setSelectedIds(prev => prev.filter(id => !decidedIds.includes(id)));
    refreshProposalList();
  }, []);

  const aiProposalSchema = useMemo(
    () => ({
      createdAt: {
        label: 'Erstellt',
        api: { itemField: 'createdAt' },
        type: 'text',
        readOnly: true,
        minWidth: '150px',
        listDisplayValue: (value, item) => formatDateTime(item?.meta?.createdAt),
      },
      serviceAccountName: {
        label: 'Service-Account',
        api: { itemField: 'serviceAccountName' },
        type: 'text',
        readOnly: true,
        minWidth: '170px',
      },
      targetDomain: {
        label: 'Domain',
        api: { itemField: 'targetDomain' },
        type: 'text',
        readOnly: true,
        minWidth: '140px',
      },
      method: {
        label: 'Methode',
        api: { itemField: 'method' },
        type: 'text',
        readOnly: true,
        minWidth: '100px',
        listDisplayValue: (value, item) => item?.action?.method || '—',
      },
      summary: {
        label: 'Zusammenfassung',
        api: { itemField: 'summary' },
        type: 'text',
        readOnly: true,
        minWidth: '280px',
      },
      status: {
        label: 'Status',
        api: { itemField: 'status' },
        type: 'text',
        readOnly: true,
        minWidth: '130px',
        listDisplayValue: value => getAiProposalStatusLabel(value),
      },
    }),
    []
  );

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx }}>
      <EditableList
        title='KI-Vorschläge'
        resourceType={RESOURCE_TYPE}
        schema={aiProposalSchema}
        searchPlaceholder='KI-Vorschläge durchsuchen'
        parentIsEditable={false}
        options={{
          disableCreate: true,
          disableDelete: true,
          disableDetailView: true,
          listConfig: {
            endpoint: '/api/v1/aiProposal',
            resultsKey: 'aiProposals',
            pageParam: 'page',
            limitParam: 'limit',
          },
          // Server-side across the whole dataset: the backend GET /api/v1/aiProposal
          // implements the header `search`, the per-column `filters` JSON param and
          // `sortKey`/`sortDirection` for every displayed column, in addition to the
          // dedicated top-control params from listParams (status, targetDomain, ...).
          serverSideFiltering: true,
          serverSideSorting: true,
        }}
        listParams={listParams}
        selection={{
          enabled: canApprove || canReject,
          selectedIds,
          onSelectedIdsChange: setSelectedIds,
          // Doubles as the hook where every rendered row is recorded, so the bulk dialog can
          // show summaries and skip rows that are no longer open.
          getRowId: row => {
            if (row?._id) rowsByIdRef.current.set(row._id, row);
            return row?._id;
          },
        }}
        renderTopControlsStart={() => (
          <Box
            sx={{
              flex: '1 1 100%',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  select
                  fullWidth
                  label='Status'
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <MenuItem value=''>Alle</MenuItem>
                  <MenuItem value='pending'>Ausstehend</MenuItem>
                  <MenuItem value='applied'>Angewendet</MenuItem>
                  <MenuItem value='rejected'>Abgelehnt</MenuItem>
                  <MenuItem value='failed'>Fehlgeschlagen</MenuItem>
                  <MenuItem value='withdrawn'>Zurückgezogen</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  fullWidth
                  label='Domain'
                  inputRef={domainInputRef}
                  placeholder='z. B. product'
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyFilters();
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
        renderTopControlsEnd={() => (
          <Stack
            direction='row'
            spacing={1}
            sx={{
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              minWidth: 0,
              justifyContent: { xs: 'stretch', sm: 'flex-start' },
              '& > .MuiButton-root': { xs: { flex: '1 1 auto' } },
            }}
          >
            {canApprove && (
              <Button
                variant='contained'
                size='small'
                disabled={actionableProposals.length === 0}
                onClick={() => openBulk('approve')}
              >
                Annehmen
                {actionableProposals.length > 0 ? ` (${actionableProposals.length})` : ''}
              </Button>
            )}
            {canReject && (
              <Button
                variant='outlined'
                color='error'
                size='small'
                disabled={actionableProposals.length === 0}
                onClick={() => openBulk('reject')}
              >
                Ablehnen
                {actionableProposals.length > 0 ? ` (${actionableProposals.length})` : ''}
              </Button>
            )}
            <Button variant='contained' size='small' onClick={applyFilters}>
              Filtern
            </Button>
            <Button variant='outlined' size='small' onClick={resetFilters}>
              Zurücksetzen
            </Button>
          </Stack>
        )}
        renderRowActions={item => (
          <>
            <Tooltip title='Details'>
              <IconButton size='small' onClick={() => openDetails(item)}>
                <VisibilityIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            {canApprove && item?.status === 'pending' && (
              <Tooltip title='Annehmen'>
                <IconButton
                  size='small'
                  color='success'
                  onClick={() => openDecision('approve', item)}
                >
                  <CheckIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {canReject && item?.status === 'pending' && (
              <Tooltip title='Ablehnen'>
                <IconButton size='small' color='error' onClick={() => openDecision('reject', item)}>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
        visibleColumnKeys={[
          'createdAt',
          'serviceAccountName',
          'targetDomain',
          'method',
          'summary',
          'status',
        ]}
      />

      <AiProposalDetailDialog
        open={detailDialog.open}
        proposal={detailDialog.proposal}
        loading={detailLoading}
        onClose={closeDetails}
        onApprove={
          canApprove ? note => handleDecision(detailDialog.proposal, 'approve', note) : null
        }
        onReject={canReject ? note => handleDecision(detailDialog.proposal, 'reject', note) : null}
      />

      <AiProposalBulkDecisionDialog
        open={bulkDialog.open}
        mode={bulkDialog.mode}
        proposals={bulkDialog.proposals}
        onCancel={closeBulk}
        onFinished={handleBulkFinished}
        runOne={runBulkOne}
      />

      <AiProposalDecisionDialog
        open={decisionDialog.open}
        mode={decisionDialog.mode}
        summary={decisionDialog.item?.summary || ''}
        onCancel={closeDecision}
        onConfirm={note => handleDecision(decisionDialog.item, decisionDialog.mode, note)}
      />
    </Box>
  );
}

export default AiProposalsView;
